<template>
  <b-card no-body>
    <b-card-body>
      <div class="field-heading color-dark-blue">
        {{ order.shipmentId ? 'Shipment' : 'Order' }} Summary
        <span class="ml-2 float-right">
          <b-button
            v-if="changeDate"
            variant="success"
            size="sm"
            class="mr-1"
            @click="onUpdateAvailabilityDate"
          >
            <feather-icon
              icon="CheckIcon"
              size="15"
            />
          </b-button>
          <b-button
            v-if="changeDate"
            variant="danger"
            size="sm"
            @click="resetDate"
          >
            <feather-icon
              icon="XIcon"
              size="15"
            />
          </b-button>
        </span>
      </div>

      <div class="field-heading">
        Total Quantity:
        <span class="field-value">{{
          formatNumber(order.totalQuantity || 0)
        }}</span>
      </div>
      <div
        v-if="shouldDisplayCreditPoints"
        class="field-heading"
      >
        Applied credit points:
        <span class="field-value">{{
          formatCurrency(order.discountInCredit, currency)
        }}</span>
      </div>
      <div class="field-heading">
        Total Cost:
        <span
          v-if="shouldDisplayCreditPoints"
          class="field-value"
        >
          {{ formatCurrency(order.totalPrice, currency) }}
        </span>
        <span
          :class="
            shouldDisplayCreditPoints &&
              'mr-5-px mt-1-px text-line-through font-small-4 font-weight-normal'
          "
          class="field-value"
        >{{ formatCurrency(order.totalPrice || 0, currency) }}</span>
      </div>
      <div class="field-heading">
        Payment Terms:
        <span class="field-value">{{ order.paymentTerms }}</span>
      </div>
      <div class="field-heading">
        <span class="text-nowrap"> Logistic Terms: </span>
        <span class="field-value">{{ order.logisticsTerms }}</span>
      </div>
      <div
        v-if="!order.shipmentId"
        class="field-heading"
      >
        Requested Delivery Window:
        <span
          v-if="!editRequestDeliveryDate"
          class="field-value"
        >
          <span v-if="order.requestedDeliveryDate">
            {{ requestedDeliveryDate }}
          </span>
          <!-- edit icon for Retailer -->
          <feather-icon
            v-if="isRetailer && canEditDate"
            icon="EditIcon"
            size="15"
            class="edit-icon"
            @click="editRequestDeliveryDate = !editRequestDeliveryDate"
          />
        </span>
        <span
          v-if="isRetailer && editRequestDeliveryDate"
          class="float-right exp-av-date"
        >
          <br class="d-block d-sm-none">
          <flat-pickr
            ref="deliveryDate"
            v-model="requestDeliveryDate"
            class="form-control exp-calender"
            :config="VUE_FLATPICKR_MONTH_CONFIG"
            :placeholder="requestDeliveryDate || KP_MONTH_FORMAT.PLACEHOLDER"
          />
          <feather-icon
            icon="CalendarIcon"
            size="20"
            class="float-right calender-icon"
            @click="togglereqDeliveryDate"
          />
        </span>
      </div>
      <div
        v-if="!order.shipmentId"
        class="field-heading"
      >
        Estimated Delivery Window:
        <span
          v-if="!onDisplayCalender"
          class="field-value"
        >{{ order.expectedAvailabilityDate }}
          <!-- edit icon for brand -->
          <feather-icon
            v-if="isBrand && canEditDate"
            icon="EditIcon"
            size="15"
            class="edit-icon"
            @click="onEdit"
          />
        </span>
        <span
          v-if="isBrand && onDisplayCalender"
          class="float-right exp-av-date"
        >
          <br class="d-block d-sm-none">
          <!-- date picker for brand -->
          <flat-pickr
            ref="expectedDate"
            v-model="order.expectedAvailabilityDate"
            class="form-control exp-calender"
            :config="VUE_FLATPICKR_MONTH_CONFIG"
            :placeholder="
              order.expectedAvailabilityDate || KP_MONTH_FORMAT.PLACEHOLDER
            "
            @on-change="onUpdateExpectedAvailabilityDate"
          />
          <feather-icon
            icon="CalendarIcon"
            size="20"
            class="float-right calender-icon"
            @click="toggleDatePicker"
          />
        </span>
      </div>
      <div
        v-if="isDiscountVisible"
        class="field-heading"
      >
        Discount:
        <span
          v-if="!isEditDiscount"
          class="field-value"
        >{{ discount }}%
          <!-- edit icon for brand -->
          <feather-icon
            v-if="isBrand"
            icon="EditIcon"
            size="15"
            class="edit-icon"
            @click="isEditDiscount = true"
          />
        </span>
        <span
          v-if="isEditDiscount"
          class="float-right exp-av-date d-flex"
        >
          <br class="d-block d-sm-none">
          <validation-provider
            #default="{ errors }"
            name="Discount"
            :rules="`between:0,100`"
            class="d-flex"
          >
            <b-form-input
              v-model="discount"
              class="w-75"
              type="number"
              size="sm"
              placeholder="Discount"
              :class="errors.length && 'danger-input'"
            />
            <b-button
              class="ml-10-px"
              size="sm"
              variant="custom-primary"
              :disabled="!!(errors.length || order.discount === discount)"
              @click="handleDiscountSave"
            >Save</b-button>
          </validation-provider>
        </span>
      </div>
    </b-card-body>
    <!-- modal expected availability date mandatory -->
    <b-modal
      v-if="isBrand"
      id="modal-exp-av-date"
      ok-variant="success"
      ok-title="Select Date"
      cancel-title="Cancel"
      modal-class="modal-success"
      centered
      title="Estimated delivery window"
      @ok="onEdit"
    >
      <b-card-text>
        Please select estimated delivery window for this order.
      </b-card-text>
    </b-modal>
    <!-- Confirm Order Discount Modal -->
    <b-modal
      v-if="isBrand"
      id="modal-confirm-order-discount"
      ok-variant="warning"
      ok-title="Confirm"
      cancel-title="Cancel"
      modal-class="modal-warning"
      centered
      title="Update Order Discount"
      @ok="confirmUpdateDiscount"
      @hide="cancelDiscountUpdate"
    >
      <b-card-text>
        This discount will apply to the entire order and remove any product-level discounts.
      </b-card-text>
      <b-card-text>
        Do you want to continue?
      </b-card-text>
    </b-modal>
  </b-card>
</template>

<script>
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { VUE_FLATPICKR_MONTH_CONFIG } from '@/constants'
import { utils } from '@kingpin-global/kingpin-utils-frontend'

import {
BButton,
BCard,
BCardBody,
BCardText,
BFormInput,
BModal,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import flatPickr from 'vue-flatpickr-component'
import { useOrderSummary } from '@/composables/useOrderSummary'
import UserRoleMixin from '../UserRoleMixin.vue'

const { formattedDate } = utils

export default {
  name: 'OrderSummary',
  components: {
    BCard,
    BCardBody,
    BButton,
    BFormInput,
    BCardText,
    BModal,
    FeatherIcon,
    flatPickr,
    ValidationProvider,
  },
  mixins: [UserRoleMixin],
  props: {
    order: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  setup(props) {
    const s = useOrderSummary()
    s.initOrderSummary(props.order)
    return {
      // State refs
      requestDeliveryDate: s.requestDeliveryDate,
      changeDate: s.changeDate,
      onDisplayCalender: s.onDisplayCalender,
      editRequestDeliveryDate: s.editRequestDeliveryDate,
      requestedDeliveryDate: s.requestedDeliveryDate,
      discount: s.discount,
      canEditDate: s.canEditDate,
      isEditDiscount: s.isEditDiscount,
      // Utilities
      formatNumber: s.formatNumber,
      formatCurrency: s.formatCurrency,
      KP_MONTH_FORMAT: s.KP_MONTH_FORMAT,
      VUE_FLATPICKR_MONTH_CONFIG,
      // Composable methods (prefixed to avoid template conflicts)
      _currency: s.currency,
      _shouldDisplayCreditPoints: s.shouldDisplayCreditPoints,
      _isDiscountVisible: s.isDiscountVisible,
      _onUpdateAvailabilityDate: s.onUpdateAvailabilityDate,
      _onUpdateExpectedAvailabilityDate: s.onUpdateExpectedAvailabilityDate,
      _hasAnyProductDiscount: s.hasAnyProductDiscount,
      _onUpdateDiscount: s.onUpdateDiscount,
      _resetDate: s.resetDate,
      onRequestedDeliveryDateChange: s.onRequestedDeliveryDateChange,
    }
  },
  computed: {
    getCurrentDate() {
      return formattedDate(new Date())
    },
    currency() {
      return this._currency(this.order)
    },
    shouldDisplayCreditPoints() {
      return this._shouldDisplayCreditPoints(this.order)
    },
    getminDate() {
      const minDate = new Date()
      const minAvailbalityDate = new Date(this.order.createdAt)
      minAvailbalityDate.setDate(minAvailbalityDate.getDate() + 7)
      return minDate > minAvailbalityDate
        ? formattedDate(minDate)
        : formattedDate(minAvailbalityDate)
    },
    isDiscountVisible() {
      return this._isDiscountVisible(this.order)
    },
  },
  watch: {
    requestDeliveryDate(newVal) {
      this.onRequestedDeliveryDateChange(newVal)
    },
  },
  emits: ['load-order'],
  methods: {
    toggleDatePicker() {
      this.$refs.expectedDate.$el._flatpickr.toggle()
    },
    togglereqDeliveryDate() {
      this.$refs.deliveryDate.$el._flatpickr.toggle()
    },
    onEdit() {
      this.onDisplayCalender = true
      setTimeout(() => {
        this.toggleDatePicker()
      }, 300)
    },
    onUpdateAvailabilityDate() {
      this._onUpdateAvailabilityDate(this.order._id)
        .then(() => {
          this.order.requestedDeliveryDate = this.requestDeliveryDate
        })
    },
    onUpdateExpectedAvailabilityDate(date) {
      this._onUpdateExpectedAvailabilityDate(date, this.order._id)
    },
    hasAnyProductDiscount() {
      return this._hasAnyProductDiscount(this.order?.products)
    },
    handleDiscountSave() {
      if (this.hasAnyProductDiscount()) {
        this.$bvModal.show('modal-confirm-order-discount')
      } else {
        this.onUpdateDiscount()
      }
    },
    cancelDiscountUpdate() {
      this.discount = this.order.discount
      this.isEditDiscount = false
    },
    confirmUpdateDiscount() {
      this.onUpdateDiscount()
    },
    onUpdateDiscount() {
      this._onUpdateDiscount(this.order._id).then(result => {
        if (result?.success) {
          this.$emit('load-order')
        }
      })
    },
    resetDate() {
      this._resetDate(this.order.requestedDeliveryDate)
    },
  },
}
</script>
